import { useAnnotationTool, type AnnotationTool } from "@/stores/annotation-tool";

/** Per-document reader view state (plan F8): the zoom + camera + active tool a
 * document was last left at, so a refresh feels like "nothing happened".
 *
 * Persisted to localStorage under one key, capped to an LRU of ~50 documents, and
 * WRITTEN debounced (~500ms) so a pan/pinch firing every frame touches storage at
 * most a couple of times. `flushViewState()` forces a pending write out on unmount
 * / page-hide; a `version` field lets the shape migrate later. The camera is
 * restored after a document's first layout; the `tool` is remembered but NOT
 * auto-applied — a document still opens in read mode (P0) and the saved tool only
 * feeds the pen-down auto-activate (F1.4, {@link rememberedTool}). */

const KEY = "graphmind.reader.viewstate";
const VERSION = 1;
const LRU_MAX = 50;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // ~30 days — older camera positions aren't restored
const DEBOUNCE_MS = 500;

export interface ReaderViewState {
  /** The live view scale the document was left at. */
  zoom: number;
  /** Screen-pixel camera translation (ViewportController tx/ty). */
  tx: number;
  ty: number;
  /** The active markup tool at save time ("none" while reading). */
  tool: AnnotationTool;
  /** Date.now() of the write — drives the staleness cap. */
  updatedAt: number;
}

interface Store {
  version: number;
  docs: Record<string, ReaderViewState>;
}

/** localStorage, or null when it is unavailable (SSR, private mode, disabled). */
function ls(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** Validate one parsed record, dropping anything malformed (never trust storage). */
function coerce(v: unknown): ReaderViewState | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isNum(r.zoom) || !isNum(r.tx) || !isNum(r.ty) || !isNum(r.updatedAt)) return null;
  const tool = typeof r.tool === "string" ? (r.tool as AnnotationTool) : "none";
  return { zoom: r.zoom, tx: r.tx, ty: r.ty, tool, updatedAt: r.updatedAt };
}

/** Read + validate the whole store from localStorage. A garbage/old-version blob
 * yields an empty store rather than throwing. */
function read(): Store {
  const store: Store = { version: VERSION, docs: {} };
  const s = ls();
  if (!s) return store;
  let raw: string | null = null;
  try {
    raw = s.getItem(KEY);
  } catch {
    return store;
  }
  if (!raw) return store;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return store;
    const p = parsed as Record<string, unknown>;
    if (p.version !== VERSION || !p.docs || typeof p.docs !== "object") return store;
    for (const [id, rec] of Object.entries(p.docs as Record<string, unknown>)) {
      const c = coerce(rec);
      if (c) store.docs[id] = c;
    }
  } catch {
    /* corrupt JSON — start fresh */
  }
  return store;
}

// In-memory source of truth, hydrated lazily from disk on first use.
let cache: Store | null = null;
function state(): Store {
  if (!cache) cache = read();
  return cache;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let hideBound = false;

/** A hard reload doesn't run React unmount cleanups, so flush the debounced write
 * on page-hide too — bound once, lazily, the first time anything is saved. */
function ensureFlushOnHide(): void {
  if (hideBound || typeof window === "undefined") return;
  hideBound = true;
  window.addEventListener("pagehide", flushViewState);
  window.addEventListener("beforeunload", flushViewState);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushViewState();
    });
  }
}

function write(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  const s = ls();
  if (!s || !cache) return;
  try {
    s.setItem(KEY, JSON.stringify(cache));
  } catch {
    /* quota exceeded / storage disabled — best effort */
  }
}

function schedule(): void {
  ensureFlushOnHide();
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    write();
  }, DEBOUNCE_MS);
}

/** The saved view state for a document, or null when there is none, it is
 * corrupt, or it is older than the staleness cap (~30 days). Callers clamp the
 * zoom into their own valid range. */
export function loadViewState(docId: string): ReaderViewState | null {
  const rec = state().docs[docId];
  if (!rec) return null;
  if (!isNum(rec.updatedAt) || Date.now() - rec.updatedAt > MAX_AGE_MS) return null;
  return rec;
}

/** Record a document's zoom/camera/tool. Coalesced (debounced) and kept to an LRU
 * of ~{@link LRU_MAX} documents; most-recently-saved wins eviction. */
export function saveViewState(docId: string, rec: ReaderViewState): void {
  const s = state();
  // Re-insert so this doc is most-recently-used (string key order = insertion).
  delete s.docs[docId];
  s.docs[docId] = rec;
  const ids = Object.keys(s.docs);
  if (ids.length > LRU_MAX) {
    for (const id of ids.slice(0, ids.length - LRU_MAX)) delete s.docs[id];
  }
  schedule();
}

/** Force any pending debounced write out now (unmount, page hide). */
export function flushViewState(): void {
  write();
}

/** The tool to auto-activate when the first pen contact lands on a read-mode
 * annotatable document (F1.4): the per-document saved tool if it was an editing
 * tool, else the store's last-used ink tool, else the pen. Never "none". */
export function rememberedTool(docId: string): AnnotationTool {
  const saved = loadViewState(docId)?.tool;
  const lastTool = useAnnotationTool.getState().lastTool;
  for (const t of [saved, lastTool]) {
    if (t && t !== "none") return t;
  }
  return "pen";
}
